import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {type AlignedData} from 'uplot';

import {startFunctionPerformanceMeasure} from '../../../util/datadog/workspace/functionPerformance';
import {type SeriesEntry} from './getSeriesEntries';

const FUNCTION_PERFORMANCE_SAMPLE_RATE = 0.05;
/** Owned by one mounted chart; no data arrays are retained. */
export type AlignmentProfileState = {
  panelId: string;
  lastAlignedYSlotCount: number;
};

export type UPlotAlignmentRumContext = {
  /** Stable panel ID within the page, not a title or metric name. */
  panelId: string;
  /** Rendered Y series, including band boundaries and original overlays. */
  seriesCount: number;
  /** Shared X-union length; every aligned Y array has this length. */
  xValueCount: number;
  /** Sum of source array lengths, including duplicates and filtered points. */
  inputPointCount: number;
  /** Longest source array; the ideal shared length for unique, unfiltered X. */
  maxInputPointCount: number;
  /** seriesCount * xValueCount: allocated Y slots including padding, not bytes. */
  alignedYSlotCount: number;
  /** maxInputPointCount / xValueCount: 1 means no shared-grid expansion. */
  alignmentEfficiency: number;
  /** alignedYSlotCount / inputPointCount: total Y storage expansion. */
  ySlotAmplification: number;
  /** Input lengths precede log filtering; exclude these events for ratio analysis. */
  xLogScale: boolean;
  /** Fraction of this chart's observed size changes selected for reporting. */
  sampleRate: 0.05;
  /** Unchanged total Y sizes do not report, even when source values change. */
  samplingUnit: 'chart-aligned-size-change';
  /** Existing alignment implementation label, not an efficiency classification. */
  alignmentPath: 'general';
};

export function getAlignedData(
  seriesEntries: (SeriesEntry | null)[],
  xLogScale?: boolean,
  profile?: AlignmentProfileState
): AlignedData {
  // Sample before taking the start timestamp so other calls avoid clock and
  // RUM-event overhead.
  const endMeasure =
    profile != null && Math.random() < FUNCTION_PERFORMANCE_SAMPLE_RATE
      ? startFunctionPerformanceMeasure('getAlignedData')
      : undefined;

  // Collect all unique x values from all lines and sort them
  const allXValues = new Set<number>();
  for (const entry of seriesEntries) {
    if (entry == null) {
      continue;
    }
    for (const point of entry.line.data) {
      const xValue = point.x;
      // Log scale cannot handle 0 or negative values - filter them out
      if (xLogScale && xValue <= 0) {
        continue;
      }
      allXValues.add(xValue);
    }
  }
  const sortedX = Array.from(allXValues).sort((a, b) => a - b);

  const xValueCount = sortedX.length;
  // Iterate through the sorted x values once to create a map of x -> index
  // to allow fast lookup of where the y value should be stored in the yArrays
  // for a given x.
  const xValueToIndex = new Map<number, number>();
  for (let i = 0; i < xValueCount; i++) {
    xValueToIndex.set(sortedX[i], i);
  }

  let inputPointCount = 0;
  let maxInputPointCount = 0;

  // For each line, create a y-values array aligned to sortedX
  // Use null where the line doesn't have data for that x value
  const yArrays = seriesEntries
    .map(entry => {
      if (entry == null) {
        return null;
      }
      const {type, line} = entry;
      if (endMeasure) {
        inputPointCount += line.data.length;
        maxInputPointCount = Math.max(maxInputPointCount, line.data.length);
      }
      // oxlint-disable-next-line unicorn/no-new-array -- https://oxc.rs/docs/guide/usage/linter/rules/unicorn/no-new-array.html
      const yValues = new Array<number | null>(xValueCount).fill(null);
      for (const point of line.data) {
        const index = xValueToIndex.get(point.x);
        if (index != null) {
          if (type === 'band-max') {
            yValues[index] = point.y0 ?? null;
          } else {
            yValues[index] = point.y;
          }
        }
      }
      return yValues;
    })
    .filter(isNotNullOrUndefined);

  if (profile != null) {
    const alignedYSlotCount = yArrays.length * xValueCount;
    const sizeChanged = alignedYSlotCount !== profile.lastAlignedYSlotCount;
    // Observe every call so unchanged sizes cannot retry an unselected sample.
    profile.lastAlignedYSlotCount = alignedYSlotCount;
    if (
      sizeChanged &&
      alignedYSlotCount > 0 &&
      inputPointCount > 0 &&
      endMeasure
    ) {
      const context: UPlotAlignmentRumContext = {
        alignmentPath: 'general',
        sampleRate: FUNCTION_PERFORMANCE_SAMPLE_RATE,
        samplingUnit: 'chart-aligned-size-change',
        panelId: profile.panelId,
        inputPointCount,
        maxInputPointCount,
        alignedYSlotCount,
        alignmentEfficiency: maxInputPointCount / xValueCount,
        ySlotAmplification: alignedYSlotCount / inputPointCount,
        seriesCount: yArrays.length,
        xLogScale: xLogScale === true,
        xValueCount,
      };
      endMeasure(context);
    }
  }
  return [sortedX, ...yArrays];
}
